﻿import React, { useState, useMemo, useEffect } from 'react'
import { Text, Heading, Input, Select, Button, SortableTableHeader, LoadingSpinner } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ServerIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../constants'
import { supabase } from '../lib/supabase'
import { modulesService } from '../services/modules'
import {
  PARCEIROS_OPTIONS,
  consultarParceiro,
  type ConsultaParceiroResponse,
} from '../services/middlewareParceiros'

interface ParceiroData {
  id: number
  partner: string
  os: string
  assistance_coc: string
  name: string
  cpf: string
  response: any
  created_at: string
}

const MiddlewareParceiros: React.FC = () => {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isConsultaModalOpen, setIsConsultaModalOpen] = useState(false)
  const [selectedParceiro, setSelectedParceiro] = useState<ParceiroData | null>(null)
  const [parceirosData, setParceirosData] = useState<ParceiroData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Estados do modal de consulta
  const [consultaParceiro, setConsultaParceiro] = useState('')
  const [consultaOS, setConsultaOS] = useState('web')
  const [consultaCPF, setConsultaCPF] = useState('')
  const [consultaNome, setConsultaNome] = useState('')
  const [consultaCodigoAssistencia, setConsultaCodigoAssistencia] = useState('')
  const [consultaSubmitting, setConsultaSubmitting] = useState(false)
  const [consultaError, setConsultaError] = useState<string | null>(null)
  const [consultaResult, setConsultaResult] = useState<ConsultaParceiroResponse | null>(null)
  
  // Estados para título e subtítulo do módulo
  const [moduleTitle, setModuleTitle] = useState<string>('Middleware Parceiros')
  const [moduleDescription, setModuleDescription] = useState<string>('Histórico de consultas e integrações com parceiros')
  
  // Estados para filtros
  const [filtroParceiro, setFiltroParceiro] = useState('')
  const [filtroNome, setFiltroNome] = useState('')
  const [filtroCPF, setFiltroCPF] = useState('')
  const [filtroOS, setFiltroOS] = useState('')
  
  // Estados de ordenação
  const [sortBy, setSortBy] = useState<'id' | 'partner' | 'os' | 'name' | 'cpf' | 'created_at'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)

  // Buscar título e descrição do módulo
  useEffect(() => {
    const loadModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('superapp_middleware_parceiros')
        if (result.success && result.data) {
          if (result.data.display_name) {
            setModuleTitle(result.data.display_name)
          }
          if (result.data.description) {
            setModuleDescription(result.data.description)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar informações do módulo:', error)
      }
    }

    loadModuleInfo()
  }, [])

  const fetchParceiros = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true)
      const { data, error } = await supabase
        .from('middleware_partners')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      const mapped = (data || []).map((row: Record<string, unknown>) => ({
        ...row,
        assistance_coc: (row.assistance_code as string) ?? (row.assistance_coc as string) ?? '',
      })) as ParceiroData[]

      setParceirosData(mapped)
      setError(null)
    } catch (err) {
      console.error('Erro ao carregar dados:', err)
      setError('Erro ao carregar dados dos parceiros')
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  useEffect(() => {
    fetchParceiros()
  }, [])

  const handleRowClick = (item: ParceiroData) => {
    setSelectedParceiro(item)
    setIsDetailsModalOpen(true)
  }

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false)
    setSelectedParceiro(null)
  }

  const resetConsultaForm = () => {
    setConsultaParceiro('')
    setConsultaOS('web')
    setConsultaCPF('')
    setConsultaNome('')
    setConsultaCodigoAssistencia('')
    setConsultaError(null)
    setConsultaResult(null)
    setConsultaSubmitting(false)
  }

  const handleOpenConsultaModal = () => {
    resetConsultaForm()
    setIsConsultaModalOpen(true)
  }

  const handleCloseConsultaModal = () => {
    setIsConsultaModalOpen(false)
    resetConsultaForm()
  }

  const selectedParceiroOption = PARCEIROS_OPTIONS.find((p) => p.id === consultaParceiro)

  const handleConsultarParceiro = async () => {
    setConsultaError(null)
    setConsultaResult(null)
    setConsultaSubmitting(true)

    try {
      const result = await consultarParceiro({
        parceiro: consultaParceiro,
        sistemaOperacional: consultaOS,
        cpfSegurado: consultaCPF,
        nomeSegurado: consultaNome,
        codigoAssistencia: consultaCodigoAssistencia,
      })
      setConsultaResult(result)
      await fetchParceiros(false)
    } catch (err) {
      setConsultaError(err instanceof Error ? err.message : 'Erro ao consultar parceiro')
    } finally {
      setConsultaSubmitting(false)
    }
  }

  const handleAbrirUrl = () => {
    if (consultaResult?.url) {
      window.open(consultaResult.url, '_blank', 'noopener,noreferrer')
    }
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as any)
      setSortOrder('asc')
    }
  }

  // Handlers de paginação
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const getResponseIcon = (response: any) => {
    if (!response) return <ServerIcon className="h-5 w-5 text-gray-500" />
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />
  }

  const getResponseText = (response: any) => {
    if (!response) return 'Sem resposta'
    return 'Com resposta'
  }

  const getResponseColor = (response: any) => {
    if (!response) return 'text-gray-600 dark:text-gray-400'
    return 'text-green-600 dark:text-green-400'
  }

  // Filtros aplicados aos dados
  const parceirosFiltrados = useMemo(() => {
    const filtered = parceirosData.filter(parceiro => {
      const partnerValue = (parceiro.partner || '').toLowerCase()
      const nameValue = (parceiro.name || '').toLowerCase()
      const cpfValue = (parceiro.cpf || '')
      const osValue = (parceiro.os || '').toLowerCase()

      const matchParceiro = filtroParceiro === '' ||
        partnerValue.includes(filtroParceiro.toLowerCase())

      const matchNome = filtroNome === '' ||
        nameValue.includes(filtroNome.toLowerCase())

      const matchCPF = filtroCPF === '' ||
        cpfValue.includes(filtroCPF)

      const matchOS = filtroOS === '' ||
        osValue.includes(filtroOS.toLowerCase())
      
      return matchParceiro && matchNome && matchCPF && matchOS
    })

    // Aplicar ordenação
    return filtered.sort((a, b) => {
      let aValue: any = a[sortBy]
      let bValue: any = b[sortBy]

      // Tratamento especial para campos específicos
      if (sortBy === 'created_at') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      } else if (sortBy === 'id') {
        aValue = Number(aValue)
        bValue = Number(bValue)
      } else {
        aValue = String(aValue).toLowerCase()
        bValue = String(bValue).toLowerCase()
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [parceirosData, filtroParceiro, filtroNome, filtroCPF, filtroOS, sortBy, sortOrder])

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [filtroParceiro, filtroNome, filtroCPF, filtroOS])

  // Calcular dados da paginação
  const totalPages = Math.ceil(parceirosFiltrados.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const parceirosPaginados = parceirosFiltrados.slice(startIndex, endIndex)

  const limparFiltros = () => {
    setFiltroParceiro('')
    setFiltroNome('')
    setFiltroCPF('')
    setFiltroOS('')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const formatJson = (data: any) => {
    if (!data) return 'Sem dados'
    try {
      // Primeiro, parse e stringify para limpar caracteres de escape
      const cleanData = JSON.parse(JSON.stringify(data))
      
      // Formatar JSON com indentação de 2 espaços
      const formatted = JSON.stringify(cleanData, null, 2)
      
      // Limpar e melhorar a formatação
      return formatted
        .replace(/\\n/g, '\n')          // Converter \n para quebras de linha reais
        .replace(/\\t/g, '  ')          // Converter \t para espaços
        .replace(/\\"/g, '"')           // Converter \" para aspas normais
        .replace(/\\\\/g, '\\')         // Converter \\ para \
        .replace(/":/g, '": ')          // Espaço após dois pontos
        .replace(/,\n/g, ',\n')         // Manter vírgulas na mesma linha
        .replace(/\{\n/g, '{\n')        // Abertura de objeto
        .replace(/\n\}/g, '\n}')        // Fechamento de objeto
    } catch (error) {
      return String(data)
    }
  }

  const formatJsonWithBold = (data: any) => {
    if (!data) return 'Sem dados'
    try {
      const formatted = formatJson(data)
      
      // Destacar nomes dos campos em negrito
      return formatted
        .replace(/"([^"]+)":/g, '"<strong>$1</strong>":')  // Nomes dos campos em negrito
    } catch (error) {
      return String(data)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <Text size="lg" color="muted">Carregando dados...</Text>
          </div>
        </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <Text size="lg" color="muted">{error}</Text>
          </div>
        </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
          <PermissionGuard 
            moduleName="superapp_middleware_parceiros" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
                </div>
              </div>
            }
          >
            <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
          <div>
            <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
              {moduleTitle}
            </Heading>
            <Text size="sm" color="muted" className="mt-2">
              {moduleDescription}
            </Text>
          </div>
          <Button
            type="button"
            onClick={handleOpenConsultaModal}
            style={{ backgroundColor: BRAND_COLOR }}
            className="shrink-0"
          >
            <PlusIcon className="h-4 w-4 mr-1.5 inline" aria-hidden />
            Nova Consulta
          </Button>
        </div>

        {/* Topo Colorido - Valores */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-6 text-white">
              <div className="text-center">
                <Text size="3xl" className="text-white font-bold mb-2">
                  {parceirosFiltrados.length}
                </Text>
                <Text size="sm" className="text-white">
                  Total de Consultas
                </Text>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
              <div className="text-center">
                <Text size="3xl" className="text-white font-bold mb-2">
                  {parceirosFiltrados.filter(p => p.response).length}
                </Text>
                <Text size="sm" className="text-white">
                  Com Resposta
                </Text>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-lg p-6 text-white">
              <div className="text-center">
                <Text size="3xl" className="text-white font-bold mb-2">
                  {parceirosFiltrados.filter(p => !p.response).length}
                </Text>
                <Text size="sm" className="text-white">
                  Sem Resposta
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="px-6 py-4">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
              Filtros
            </Heading>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  Parceiro
                </Text>
                <Input
                  type="text"
                  placeholder="Digite o nome do parceiro..."
                  value={filtroParceiro}
                  onChange={(e) => setFiltroParceiro(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  Nome
                </Text>
                <Input
                  type="text"
                  placeholder="Digite o nome..."
                  value={filtroNome}
                  onChange={(e) => setFiltroNome(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  CPF
                </Text>
                <Input
                  type="text"
                  placeholder="Digite o CPF..."
                  value={filtroCPF}
                  onChange={(e) => setFiltroCPF(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  OS
                </Text>
                <Select
                  value={filtroOS}
                  onChange={(e) => setFiltroOS(e.target.value)}
                >
                  <option value="">Todas as OS</option>
                  <option value="ios">iOS</option>
                  <option value="android">Android</option>
                  <option value="web">Web</option>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={limparFiltros}
                  className="w-full"
                  style={{ backgroundColor: BRAND_COLOR }}
                >
                  Limpar Filtros
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabela - Ocupando todo o espaço restante */}
        <div className="flex-1 p-6 pt-0">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden h-full flex flex-col">
            <div className="px-4 py-5 sm:p-6 flex-1 flex flex-col">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                Histórico de Consultas
              </Heading>
              
              <div className="flex-1 overflow-auto">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      <SortableTableHeader
                        sortKey="partner"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="w-1/6"
                      >
                        Parceiro
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="name"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="w-1/6"
                      >
                        Nome
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="cpf"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="w-1/6 text-center"
                      >
                        CPF
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="os"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="w-1/6 text-center"
                      >
                        OS
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="created_at"
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="w-1/6 text-center"
                      >
                        Data
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                        Sucesso
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {parceirosPaginados.map((item) => (
                      <tr 
                        key={item.id} 
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={() => handleRowClick(item)}
                      >
                        <td className="px-4 py-4 text-left">
                          <Text size="sm" className="text-gray-900 dark:text-white font-medium truncate block">
                            {item.partner}
                          </Text>
                        </td>
                        <td className="px-4 py-4 text-left">
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">
                            {item.name}
                          </Text>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            {item.cpf}
                          </Text>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            {item.os}
                          </Text>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            {formatDate(item.created_at)}
                          </Text>
                        </td>
                        <td className="px-4 py-4 text-left">
                          <div className="flex items-center">
                            {getResponseIcon(item.response)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Controles de Paginação */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {startIndex + 1} a {Math.min(endIndex, parceirosFiltrados.length)} de {parceirosFiltrados.length} registros
                        </Text>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNumber: number;
                            if (totalPages <= 5) {
                              pageNumber = i + 1;
                            } else if (currentPage <= 3) {
                              pageNumber = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + i;
                            } else {
                              pageNumber = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChange(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPage === pageNumber
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Consulta */}
      {isConsultaModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseConsultaModal}
            />

            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Nova Consulta
                  </Heading>
                  <Text size="sm" color="muted" className="mt-1">
                    Acionar middleware de parceiros
                  </Text>
                </div>
                <button
                  type="button"
                  onClick={handleCloseConsultaModal}
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4 overflow-auto flex-1">
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Parceiro <span className="text-red-500">*</span>
                  </Text>
                  <Select
                    value={consultaParceiro}
                    onChange={(e) => setConsultaParceiro(e.target.value)}
                  >
                    <option value="">Selecione um parceiro...</option>
                    {PARCEIROS_OPTIONS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome} ({p.id})
                      </option>
                    ))}
                  </Select>
                </div>

                {(selectedParceiroOption?.requiresOs || consultaParceiro) && (
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Sistema Operacional
                      {selectedParceiroOption?.requiresOs && (
                        <span className="text-red-500"> *</span>
                      )}
                    </Text>
                    <Select value={consultaOS} onChange={(e) => setConsultaOS(e.target.value)}>
                      <option value="web">Web</option>
                      <option value="ios">iOS</option>
                      <option value="android">Android</option>
                    </Select>
                  </div>
                )}

                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    CPF do Segurado
                    {selectedParceiroOption?.requiresCpf && (
                      <span className="text-red-500"> *</span>
                    )}
                  </Text>
                  <Input
                    type="text"
                    placeholder="Digite o CPF..."
                    value={consultaCPF}
                    onChange={(e) => setConsultaCPF(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Nome do Segurado
                  </Text>
                  <Input
                    type="text"
                    placeholder="Digite o nome..."
                    value={consultaNome}
                    onChange={(e) => setConsultaNome(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Código de Assistência
                  </Text>
                  <Input
                    type="text"
                    placeholder="Opcional..."
                    value={consultaCodigoAssistencia}
                    onChange={(e) => setConsultaCodigoAssistencia(e.target.value)}
                    className="w-full"
                  />
                </div>

                {consultaError && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                    <Text size="sm" className="text-red-700 dark:text-red-300">
                      {consultaError}
                    </Text>
                  </div>
                )}

                {consultaResult && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 shrink-0" />
                      <Text size="sm" className="text-green-700 dark:text-green-300">
                        Consulta realizada com sucesso
                      </Text>
                    </div>

                    {consultaResult.url && (
                      <div className="flex items-center gap-2">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 truncate flex-1">
                          {consultaResult.url}
                        </Text>
                        <Button type="button" variant="outline" size="sm" onClick={handleAbrirUrl}>
                          <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1 inline" aria-hidden />
                          Abrir URL
                        </Button>
                      </div>
                    )}

                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium">
                          Resposta JSON
                        </Text>
                      </div>
                      <div className="p-4">
                        <div
                          className="text-sm text-gray-900 dark:text-gray-100 font-mono overflow-auto max-h-48 whitespace-pre-wrap break-words leading-loose bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border"
                          dangerouslySetInnerHTML={{ __html: formatJsonWithBold(consultaResult) }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button type="button" variant="outline" onClick={handleCloseConsultaModal}>
                  Fechar
                </Button>
                <Button
                  type="button"
                  onClick={handleConsultarParceiro}
                  disabled={consultaSubmitting || !consultaParceiro}
                  style={{ backgroundColor: BRAND_COLOR }}
                >
                  {consultaSubmitting ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2 inline" />
                      Consultando...
                    </>
                  ) : (
                    'Consultar'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {isDetailsModalOpen && selectedParceiro && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDetailsModal}></div>
            
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:p-6 max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Detalhes da Consulta
                  </Heading>
                  <Text size="sm" color="muted" className="mt-1">
                    {selectedParceiro.partner} - {selectedParceiro.name}
                  </Text>
                </div>
                <button
                  onClick={handleCloseDetailsModal}
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Tabela de Configuração */}
              <div className="flex-1 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Campo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          ID
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedParceiro.id}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Parceiro
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedParceiro.partner}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Nome
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedParceiro.name}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          CPF
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-mono">
                          {selectedParceiro.cpf}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          OS
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedParceiro.os}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Código de Assistência
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedParceiro.assistance_coc}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Resposta
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="max-w-7xl">
                          <div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                  <Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium ml-2">
                                    Resposta JSON
                                  </Text>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-500">
                                  JSON
                                </div>
                              </div>
                            </div>
                            <div className="p-6">
                              <div
                                className="text-sm text-gray-900 dark:text-gray-100 font-mono overflow-auto max-h-96 whitespace-pre-wrap break-words leading-loose bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border"
                                dangerouslySetInnerHTML={{ __html: formatJsonWithBold(selectedParceiro.response) }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Data de Criação
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {formatDate(selectedParceiro.created_at)}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Sucesso
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="flex items-center">
                          {getResponseIcon(selectedParceiro.response)}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
          </PermissionGuard>
        </main>
      </div>
  )
}

export default MiddlewareParceiros
